import {
  Box,
  Button,
  Link,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useRouter } from 'next/navigation'
import type React from 'react'
import { useState } from 'react'
import type { LoginResponse } from '../../../client'
import useAuth from '../../hooks/useAuth'
import { sanitizeInternalRedirect } from '../../utils/safeRedirect'

interface LoginModalProps {
  open: boolean
  onClose: () => void
}

const LoginModal: React.FC<LoginModalProps> = ({ open, onClose }) => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [loginHistoryId, setLoginHistoryId] = useState<number | null>(null)
  const [userId, setUserId] = useState<number | null>(null)
  const router = useRouter()

  const {
    loginMutation,
    handleOtpSubmit,
    otpError,
    setOtpError,
    requiresOtp,
    error,
    resetOtpRequirement,
    resetError,
  } = useAuth()

  const [isLoading, setIsLoading] = useState(false)

  const handleLogin = async () => {
    setIsLoading(true)
    try {
      resetError()
      const response: LoginResponse = await loginMutation.mutateAsync({
        username,
        password,
      })

      if (response.requiresOtp) {
        setLoginHistoryId(response.loginHistoryId ?? null)
        setUserId(response.userId ?? null)
      } else {
        onClose()

        // Check if there's a redirect URL stored from the login flow
        const redirectUrl = sanitizeInternalRedirect(
          localStorage.getItem('loginRedirectUrl'),
          window.location.origin,
        )
        if (redirectUrl) {
          localStorage.removeItem('loginRedirectUrl') // Clear the stored URL

          // Use a slightly longer delay to ensure the modal is fully closed
          setTimeout(() => {
            router.push(redirectUrl)
          }, 300)
        }
      }
    } catch (err: any) {
      // console.error('Login failed:', err);
      //console.log('Full error object:', JSON.stringify(err, null, 2));

      const errorMessage =
        err.response?.detail ||
        err.message || // Extract detail from the response or normalized error
        'An unexpected error occurred. Please try again.' // Fallback message

      resetError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpSubmission = async () => {
    if (!loginHistoryId || !userId) {
      console.warn('Missing loginHistoryId or userId during OTP submission')
      setOtpError('Invalid login attempt. Please try logging in again.')
      return
    }

    setIsLoading(true)
    try {
      resetError()
      await handleOtpSubmit(userId, otpCode, loginHistoryId)
      if (!otpError) {
        onClose()

        // Check if there's a redirect URL stored from the login flow
        const redirectUrl = sanitizeInternalRedirect(
          localStorage.getItem('loginRedirectUrl'),
          window.location.origin,
        )
        if (redirectUrl) {
          localStorage.removeItem('loginRedirectUrl') // Clear the stored URL

          // Use a slightly longer delay to ensure the modal is fully closed
          setTimeout(() => {
            router.push(redirectUrl)
          }, 300)
        }
      }
    } catch (err) {
      console.error('OTP submission failed:', err)
      setOtpError('OTP submission failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Clean up any stored redirect URL when the modal is closed manually
  const handleModalClose = () => {
    // Only clear the redirectUrl if we're not actively redirecting
    // This prevents race conditions when manually closing after successful login
    if (!isLoading) {
      localStorage.removeItem('loginRedirectUrl')
    }
    onClose()
  }

  return (
    <>
      {/* Main Login Modal */}
      <Modal open={open} onClose={handleModalClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            backgroundColor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" component="h2">
            Login
          </Typography>
          <TextField
            id="username"
            label="Email"
            fullWidth
            margin="normal"
            variant="outlined"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            id="password"
            label="Password"
            fullWidth
            margin="normal"
            variant="outlined"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && (
            <Typography color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                onClose()
                router.push('/recover-password')
              }}
              sx={{ cursor: 'pointer' }}
            >
              Forgot Password?
            </Link>
          </Box>
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button color="primary" onClick={handleLogin}>
              Login
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </Stack>
        </Box>
      </Modal>

      {/* OTP Modal */}
      <Modal open={requiresOtp} onClose={resetOtpRequirement}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            backgroundColor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" component="h2">
            Enter OTP
          </Typography>
          <TextField
            id="otp_code"
            label="OTP Code"
            fullWidth
            margin="normal"
            variant="outlined"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value)}
          />
          {otpError && (
            <Typography color="error" sx={{ mt: 1 }}>
              {otpError}
            </Typography>
          )}
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button color="primary" onClick={handleOtpSubmission}>
              Submit OTP
            </Button>
            <Button onClick={resetOtpRequirement}>Cancel</Button>
          </Stack>
        </Box>
      </Modal>
    </>
  )
}

export default LoginModal
